import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IProductCategoryItem } from 'services/shop/types/product';
import styled from 'styled-components';

interface Props {
  data: IProductCategoryItem;
  isActive: boolean;
  onUpdate?: (data: IProductCategoryItem) => void;
  onChange?: (value: string) => void;
  onDelete?: (data: IProductCategoryItem) => void;
}

const CategoriesSortableItem = ({ data, isActive, onChange, onUpdate, onDelete }: Props) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: data.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: 'move',
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <CategoriesSortableItemStyled
        className={isActive ? 'active' : ''}
        onClick={() => onChange?.(data.id)}
      >
        <div className='icon-drag'>
          <IconDrag />
        </div>
        {isActive ? (
          <div className='category-container'>
            <div className='category-name-container-active'>
              <span className='category-name'>{data.categoryName}</span>
              <span className='service-count'>{data.products?.length ?? 0} items</span>
            </div>
            <div className='icon-box-container'>
              <div className='icon-box delete' onClick={() => onDelete?.(data)}>
                <IcDelete />
              </div>
              <div className='icon-box edit' onClick={() => onUpdate?.(data)}>
                <IcEdit />
              </div>
            </div>
          </div>
        ) : (
          <div className='category-container'>
            <span className='category-name'>{data.categoryName}</span>
            <span className='service-count'>{data.products?.length ?? 0} items</span>
          </div>
        )}
      </CategoriesSortableItemStyled>
    </div>
  );
};

export default CategoriesSortableItem;

const CategoriesSortableItemStyled = styled.div`
  display: flex;
  min-height: 77px;
  padding: 0 16px;
  align-items: center;
  gap: 4px;
  align-self: stretch;
  border: 1px solid #e9edf2;
  background: #fcfcfd;
  box-shadow: 0 2px 4px 0 rgba(44, 35, 66, 0.4), 0 4px 8px -3px rgba(45, 35, 66, 0.3),
    0 -3px 0 0 #d6d6e7 inset;
  .icon-drag {
    display: flex;
    width: 32px;
    height: 32px;
    justify-content: center;
    align-items: center;
    gap: 10px;
    aspect-ratio: 1/1;
  }
  .category-container {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    align-items: center;
    flex: 1 0 0;
    .category-name {
      color: #1d2129;
      font-family: Poppins;
      font-size: 20px;
      font-style: normal;
      font-weight: 500;
      line-height: normal;
    }
    .service-count {
      color: #505050;
      font-family: Poppins;
      font-size: 18px;
      font-style: normal;
      font-weight: 400;
      line-height: normal;
    }
  }
  &.active .category-container {
    .category-name {
      color: #fff;
    }
    .service-count {
      color: #cbd5e1;
    }
  }
  &.active {
    border: 1px solid #2d6772;
    background: #232f3e;
  }
  .category-name-container-active {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .icon-box-container {
    cursor: pointer;
    display: flex;
    gap: 16px;
  }
  .icon-box {
    display: flex;
    width: 40px;
    height: 40px;
    justify-content: center;
    align-items: center;
    gap: 10px;
    border-radius: 4px;
    &.delete {
      background: #fff1f6;
    }
    &.edit {
      background: #c6e9ef;
    }
  }
`;

const IconDrag = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='12' height='17' viewBox='0 0 12 17' fill='none'>
    <circle cx='2.71795' cy='2.09615' r='1.84615' fill='#777D7C' />
    <ellipse cx='9.28204' cy='2.09615' rx='1.84615' ry='1.84615' fill='#777D7C' />
    <ellipse cx='2.71795' cy='8.24996' rx='1.84615' ry='1.84615' fill='#777D7C' />
    <ellipse cx='9.28204' cy='8.24996' rx='1.84615' ry='1.84615' fill='#777D7C' />
    <ellipse cx='2.71795' cy='14.4039' rx='1.84615' ry='1.84615' fill='#777D7C' />
    <ellipse cx='9.28204' cy='14.4039' rx='1.84615' ry='1.84615' fill='#777D7C' />
  </svg>
);

const IcDelete = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='21' height='31' viewBox='0 0 21 31' fill='none'>
    <path
      d='M20.1295 10.1373C13.8271 10.1373 7.53453 10.1373 1.2322 10.1373C1.19335 10.1373 1.15451 10.1373 1.10596 10.1373C1.15451 10.5548 1.20307 10.9627 1.25162 11.3705C1.25162 11.3803 1.25162 11.3997 1.25162 11.4094C1.27104 11.5745 1.29046 11.7396 1.30988 11.9046L1.5041 13.5458C1.56237 14.0702 1.63034 14.5945 1.68861 15.1092C1.82456 16.2842 1.96051 17.4495 2.10617 18.6245C2.21299 19.5568 2.32952 20.489 2.43634 21.4213C2.57229 22.5768 2.70824 23.7324 2.8442 24.888C2.99957 26.1699 3.14523 27.4517 3.3006 28.7238C3.33945 29.0345 3.37829 29.3453 3.40742 29.6463C3.45598 30.0833 3.7473 30.3552 4.18429 30.3552C5.06798 30.3552 5.95166 30.3552 6.84506 30.3552C6.96159 30.3552 7.01985 30.2775 7.09754 30.2096C7.10725 30.1998 7.09754 30.1707 7.09754 30.1416C7.03927 30.1707 6.99072 30.1901 6.93245 30.2096C6.91303 30.0736 6.9713 30.0348 7.06841 30.0251C7.1558 30.0153 7.22378 30.0736 7.23349 30.1707C7.23349 30.1901 7.23349 30.2096 7.23349 30.229C7.21407 30.3358 7.27233 30.3552 7.36944 30.3552C8.69011 30.3552 10.0108 30.3552 11.3315 30.3552C12.1569 30.3552 12.9823 30.3552 13.8174 30.3552C13.9437 30.3552 14.0116 30.2775 14.099 30.2193C14.1088 30.2096 14.1088 30.1707 14.1088 30.1513C14.0796 30.1513 14.0699 30.1513 14.0602 30.161C14.0311 30.1804 13.9922 30.1998 13.9631 30.2096C13.9631 30.2096 13.934 30.1804 13.934 30.1707C13.9534 30.1319 13.9728 30.0833 14.0019 30.0639C14.1088 29.9959 14.2253 30.0639 14.235 30.1901C14.235 30.229 14.2253 30.2678 14.2447 30.297C14.2544 30.3261 14.2933 30.3552 14.3224 30.3746C14.3321 30.3844 14.3612 30.3746 14.3807 30.3746C15.3226 30.3746 16.2646 30.3746 17.2162 30.3746C17.7018 30.3746 17.9737 30.1319 18.0319 29.656C18.1873 28.3159 18.3524 26.9759 18.5078 25.626C18.6826 24.15 18.8574 22.674 19.0321 21.1979C19.1875 19.8578 19.3429 18.5177 19.508 17.1679C19.6633 15.8181 19.8284 14.4683 19.9935 13.1185C20.0032 13.002 20.0227 12.8854 20.0324 12.7592L20.2266 11.1472C20.246 10.9821 20.2654 10.8267 20.2848 10.6714C20.2946 10.5743 20.314 10.4772 20.3334 10.38C20.3334 10.2926 20.3334 10.2053 20.3334 10.1276C20.246 10.1276 20.1877 10.1373 20.1295 10.1373ZM7.54424 27.8304C7.07812 27.8692 6.67026 27.5294 6.60229 27.0535C6.42749 25.6649 6.2527 24.2762 6.0779 22.8973C5.92253 21.6155 5.75744 20.3434 5.59236 19.0615C5.43699 17.8574 5.29132 16.6532 5.14566 15.4491C5.03884 14.614 4.94173 13.7885 4.83491 12.9534C4.76694 12.4679 5.09711 12.0115 5.57294 11.9435C6.06819 11.8755 6.51489 12.196 6.58286 12.6912C6.69939 13.5943 6.81592 14.4974 6.92274 15.4103C7.05869 16.5076 7.19465 17.6049 7.3306 18.7022C7.50539 20.1103 7.68019 21.5184 7.85498 22.9167C8.01036 24.1888 8.17544 25.4707 8.33081 26.7428C8.34052 26.8108 8.34052 26.8787 8.35024 26.937C8.36966 27.4128 8.01036 27.7916 7.54424 27.8304ZM10.8556 27.879C10.3604 27.9664 9.87484 27.5973 9.83599 27.1021C9.82628 27.0244 9.82628 26.937 9.82628 26.8593C9.82628 23.1595 9.82628 19.4694 9.82628 15.7696C9.82628 15.7113 9.82628 15.6627 9.82628 15.6045V15.4394C9.82628 15.4491 9.82628 15.4685 9.82628 15.4782C9.82628 14.5848 9.82628 13.6914 9.82628 12.8078C9.82628 12.429 9.98165 12.1377 10.3312 11.9726C10.8848 11.7007 11.5451 12.0794 11.5937 12.6912C11.5937 12.7495 11.5937 12.8078 11.5937 12.866C11.5937 13.3613 11.5937 13.8565 11.5937 14.3518V16.1191C11.5937 19.7316 11.5937 23.3343 11.5937 26.9467C11.5937 27.442 11.3217 27.7916 10.8556 27.879ZM16.5947 12.9049C16.4685 13.9731 16.3325 15.0315 16.1966 16.0997C16.0509 17.2553 15.9053 18.4109 15.7596 19.5665C15.6042 20.8386 15.4391 22.1107 15.2838 23.3828C15.1284 24.5967 14.9827 25.8106 14.8274 27.0244C14.7788 27.442 14.4778 27.7624 14.099 27.8304C13.6912 27.8984 13.3125 27.6945 13.1377 27.3254C13.0503 27.1312 13.0503 26.937 13.0794 26.7331C13.2639 25.257 13.4484 23.7713 13.6329 22.2952C13.7883 21.0814 13.934 19.8772 14.0893 18.6634L14.235 17.4981C14.2447 17.4398 14.2544 17.3718 14.2544 17.3136C14.4292 15.8958 14.604 14.4877 14.7885 13.0699C14.8079 12.9437 14.8176 12.8078 14.8371 12.6815C14.9148 12.2057 15.3517 11.8852 15.8276 11.9532C16.3131 12.0018 16.653 12.429 16.5947 12.9049Z'
      fill='#DA072D'
    />
    <path
      d='M1.38771 8.07878C3.32016 7.51555 5.25262 6.95232 7.17536 6.38909L7.23363 6.36967C10.545 5.39859 13.837 4.43722 17.1387 3.47584L17.4786 3.37874C17.4883 3.37874 17.498 3.37874 17.5077 3.37874V3.36903L17.7116 3.31076L17.9738 3.23307C17.9738 3.23307 17.9835 3.23307 17.9835 3.24278L17.9932 3.23307L18.4982 3.08741C18.5079 3.08741 18.5176 3.08741 18.5176 3.08741C18.5176 3.08741 18.5176 3.0777 18.5273 3.0777L18.7507 3.00973C18.9158 2.96117 19.0808 2.91262 19.2362 2.86406H19.2459C19.3527 2.83493 19.4596 2.79609 19.5761 2.76695C19.4304 2.25228 19.2751 1.75703 19.1391 1.26178C19.0129 0.805367 18.6147 0.591729 18.168 0.717969C16.7794 1.12582 15.4004 1.53368 14.0118 1.93182C13.8467 1.98038 13.6816 2.02893 13.5165 2.06777C13.4971 2.01922 13.4874 1.98038 13.4777 1.95124C13.3417 1.49484 13.2155 1.03843 13.0795 0.591729C12.9824 0.271271 12.7008 0.0867656 12.3609 0.125609C12.2735 0.13532 12.1862 0.164452 12.0988 0.193585C10.108 0.776234 8.13673 1.34917 6.16544 1.92211C5.76729 2.03864 5.5925 2.37852 5.70903 2.77667C5.83527 3.22336 5.97122 3.67006 6.09746 4.11676C6.12659 4.20416 6.10717 4.243 6.01977 4.26242C5.55365 4.39837 5.07782 4.53433 4.6117 4.67028C3.56293 4.98102 2.51416 5.29177 1.45568 5.59281C1.26147 5.65107 1.10609 5.74818 1.00899 5.92297C0.960432 6.01037 0.931299 6.11719 0.892456 6.20459C0.892456 6.26285 0.892456 6.33083 0.892456 6.38909C0.979854 6.68042 1.06725 6.97174 1.15465 7.27278C1.23234 7.54468 1.31002 7.80687 1.38771 8.07878ZM11.8074 1.79587C11.8754 2.03864 11.9434 2.26199 12.0114 2.50476C11.9725 2.52418 11.9337 2.53389 11.8948 2.54361C11.5452 2.65042 11.1859 2.75724 10.8363 2.85435C10.8266 2.85435 10.8169 2.86406 10.8072 2.86406L9.70989 3.18452C9.01071 3.38845 8.31153 3.59237 7.60264 3.7963C7.53466 3.55353 7.46669 3.33018 7.389 3.0777C8.86505 2.65042 10.3314 2.22315 11.8074 1.79587Z'
      fill='#DA072D'
    />
  </svg>
);

const IcEdit = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32' fill='none'>
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M0 31.0596H31.4821V28.0761H0V31.0596ZM11.0187 20.6174V16.6046L24.5151 4.61404L27.3973 7.65869L15.0563 20.6174H11.0187ZM32 7.66166L24.6017 0.583374L7.87054 15.3456V23.6009H16.4037L32 7.66166Z'
      fill='#232F3E'
    />
  </svg>
);
